import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const navItems = [
  { path: '/', label: 'Home' },
  { path: '/research', label: 'Research' },
  { path: '/publications', label: 'Publications' },
  { path: '/people', label: 'People' },
  { path: '/talks', label: 'Talks' },
  { path: '/events', label: 'Events' },
  { path: '/location', label: 'Location' },
  { path: '/journal-club', label: 'Journal Club' },
];

function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  // Close menu on escape
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') setMenuOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  // Lock scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <div className="page-wrapper">
      <header className="site-header">
        <div className="header-inner">
          <NavLink to="/" className="site-logo">
            <div className="logo-mark">A</div>
            <div className="logo-text">
              Astrophysics Group
              <span>Center for Theoretical Physics · PAS</span>
            </div>
          </NavLink>

          <button
            className="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <nav className="desktop-nav">
            <ul className="nav-links">
              {navItems.map(({ path, label }) => (
                <li key={path}>
                  <NavLink
                    to={path}
                    end={path === '/'}
                    className={({ isActive }) => isActive ? 'active' : ''}
                  >
                    {label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      {/* Mobile nav rendered OUTSIDE header to avoid backdrop-filter containing block */}
      <nav className={`mobile-nav${menuOpen ? ' open' : ''}`}>
        <ul className="mobile-nav-links">
          {navItems.map(({ path, label }) => (
            <li key={path}>
              <NavLink
                to={path}
                end={path === '/'}
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <main style={{ flex: 1, position: 'relative', zIndex: 1 }}>
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-inner">
            <div className="footer-text">
              © {new Date().getFullYear()} Astrophysics Group · Center for Theoretical Physics, Polish Academy of Sciences
            </div>
            <ul className="footer-links">
              <li><a href="https://www.cft.edu.pl" target="_blank" rel="noopener noreferrer">CTP PAS</a></li>
              <li><a href="https://www.ifpan.edu.pl" target="_blank" rel="noopener noreferrer">IF PAN Campus</a></li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
